/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import classnames from 'classnames';
import * as React from 'react';
import { IRequestContext } from '../../interfaces';
import { isEditorialMode } from '../../utilities/editable-fields/utils';

import {
    MainImage,
    EmptyPlaceholderImage,
    LoadingPlaceholderImage,
    INativeImageProps,
    ThumbnailImage,
    IMainImageState,
    IThumbnailImageState
} from './image-types';

/**
 * Represents state of the image
 */
export interface IImageState {
    /**
     * Defines if loading placeholder should be hidden.
     */
    isLoadingPlaceholderHidden: boolean;

    /**
     * Defines if thumbnail image downloading was finished.
     * True if image is either ready to be displayed to the user or the image is broken and failed to be downloaded.
     */
    isThumbnailLoadingFinished: boolean;
    /**
     * Defines if thumbnail image failed to be downloaded.
     * True if the image is broken or unaccessible.
     */
    isThumbnailLoadingFailed: boolean;
    /**
     * Defines if thumbnail should be hidden.
     */
    isThumbnailHidden: boolean;

    /**
     * Defines if main image downloading was finished.
     * True if image is either ready to be displayed to the user or the image is broken and failed to be downloaded.
     */
    isMainImageLoadingFinished: boolean;
    /**
     * Defines if thumbnail image failed to be downloaded.
     * True if the image is broken or unaccessible.
     */
    isMainImageLoadingFailed: boolean;

    /**
     * The source of the original main image (does not guarantee the image exists).
     */
    src: string;
}

/**
 * Configuration for the image component.
 */
export interface IImageProps extends INativeImageProps {
    /**
     * Defines if thumbnail should be used as intermediate image while the main image is loading.
     * The setting will be applied only to the given image.
     *
     * In case the global app configuration specified the thumbnails should be turned off,
     * this configuration will be ignored and thumbnail will not be shown even if it was passed as true.
     * True by default.
     */
    shouldUseThumbnails?: boolean;

    /**
     * Defines if native image logic should be used.
     * False by default.
     */
    shouldSkipToMainImage?: boolean;

    /**
     * Request context. Used to retrieve app settings.
     */
    requestContext?: IRequestContext;
}

/**
 * React component responsible for rendering the image and defining the default behavior for the image.
 * 1. A loading placeholder image is used to be displayed while the main image is loading.
 * 2. In case thumbnails should be used, the loading placeholder will be replaced with the thumbnail once the thumbnail is loaded while the main image is still loading.
 * 3. Once the main image is loaded, it will be displayed over the loading placeholder and thumbnails.
 * In case the thumbnail fails to load, the main image will not be downloaded and an empty placeholder image will be displayed.
 * In case the main image fails to load, an empty placeholder image will be displayed.
 */
export class Image extends React.Component<IImageProps, IImageState> {
    /**
     * The default class for image component container.
     */
    private static _className: string = 'msc-image-container';

    /**
     * The default class for each specific image (placeholders, thumbnails, original images).
     */
    private static _imageClassName: string = 'msc_image';

    /**
     * The class to use to mark the loading state of a specific sub-image.
     * This defines that the given sub-image is not yet ready to be displayed to the user and should be hidden.
     */
    private static _loadingStateClassName: string = 'loading';

    /**
     * The class to use to mark the hidden state of a specific sub-image.
     * This defines that the given sub-image is not used and should not be displayed to the user.
     */
    private static _hiddenStateClassName: string = 'hidden';

    /**
     * The delay to hide loading placeholder image while the already downloaded thumbnail/main image is rendering on the page.
     * Used to prevent lags due to the behavior of a browser.
     */
    private static _defaultLoadingPlaceholderHidingDelay: number = 300;

    /**
     * The delay to hide the thumbnail image while the already downloaded main image is rendering on the page.
     * Used to prevent lags due to the behavior of a browser.
     */
    private static _defaultThumbnailHidingDelay: number = 3000;

    /**
     * The timer to hide loading placeholder. Initialized once thumbnail/main image is downloaded.
     */
    private _hideLoadingPlaceholderTimeout?: NodeJS.Timeout;
    /**
     * The timer to hide thumbnail. Initialized once main image is downloaded.
     */
    private _hideThumbnailTimeout?: NodeJS.Timeout;

    /**
     * Setting which specifies if thumbnails approach should be used.
     */
    private _shouldUseThumbnails: boolean;

    /**
     * Defines if native image logic should be used.
     */
    private _shouldSkipToMainImage: boolean;

    /**
     * Initializes component with the given props.
     * @param {IImageProps} props - Configuration for the image component.
     */
    public constructor(props: IImageProps) {
        super(props);

        const localShouldUseThumbnailsSetting = props.shouldUseThumbnails ?? true;
        const globalShouldUseThumbnailsSetting = props.requestContext?.app?.config?.shouldUseThumbnails ?? true;
        this._shouldUseThumbnails = localShouldUseThumbnailsSetting && globalShouldUseThumbnailsSetting;

        const localShouldSkipToMainImage =
            isEditorialMode(props.editProps?.requestContext) ||
            props.imageSettings?.disableLazyLoad ||
            (props.shouldSkipToMainImage ?? false);

        const globalShouldUseNewImageComponent = props.requestContext?.app?.config?.shouldUseNewImageComponent ?? false;
        this._shouldSkipToMainImage =
            !globalShouldUseNewImageComponent ||
            localShouldSkipToMainImage ||
            !!this.props.requestContext?.features?.disable_image_componentv2;

        this.state = this.getInitialState();

        this.onMainImageUpdate = this.onMainImageUpdate.bind(this);
        this.onThumbnailImageUpdate = this.onThumbnailImageUpdate.bind(this);
    }

    /**
     * Creates a new default image state.
     */
    public getInitialState(): IImageState {
        return {
            src: this.props.src || '',

            isLoadingPlaceholderHidden: false,

            isThumbnailLoadingFinished: false,
            isThumbnailLoadingFailed: false,
            isThumbnailHidden: false,

            isMainImageLoadingFinished: false,
            isMainImageLoadingFailed: false
        };
    }

    /**
     * In case component was updated and was given new props, the component's state should be reset.
     * @param {IImageProps} prevProps - Previous props that were used in this component.
     */
    public componentDidUpdate(prevProps: IImageProps): void {
        if (this.props.src !== prevProps.src || this.props.fallBackSrc !== prevProps.fallBackSrc) {
            this.setState(this.getInitialState());

            if (this._hideThumbnailTimeout) {
                clearTimeout(this._hideThumbnailTimeout);
            }

            if (this._hideLoadingPlaceholderTimeout) {
                clearTimeout(this._hideLoadingPlaceholderTimeout);
            }
        }
    }

    /**
     * Listens to main image updates to know when the main image is loaded or failed to be loaded.
     * @param {IMainImageState} state - The state of the main image.
     */
    public onMainImageUpdate(state: IMainImageState): void {
        this.setState({
            isMainImageLoadingFinished: state.loaded || state.loadFailed,
            isMainImageLoadingFailed: state.loadFailed
        });

        if (!this.state.isThumbnailHidden && state.loaded) {
            this._setThumbnailHideTimeout();
        }

        if (!this.state.isLoadingPlaceholderHidden && state.loaded) {
            this._setLoadingPlaceholderHideTimeout();
        }
    }

    /**
     * Listens to thumbnail image updates to know when the thumbnail image is loaded or failed to be loaded.
     * @param {IThumbnailImageState} state - The state of the thumbnail image.
     */
    public onThumbnailImageUpdate(state: IThumbnailImageState): void {
        this.setState({
            isThumbnailLoadingFinished: state.loaded || state.loadFailed,
            isThumbnailLoadingFailed: state.loadFailed
        });
        if (!this.state.isLoadingPlaceholderHidden && state.loaded) {
            this._setLoadingPlaceholderHideTimeout();
        }
    }

    /**
     * Renders image component in a container.
     */
    public render(): React.ReactNode {
        if (this._shouldSkipToMainImage) {
            return this._renderMainImage(false, true);
        }

        if (!this.state.src) {
            return null;
        }

        return (
            <div className={Image._className} key={this.state.src}>
                {this._getImageContent()}
            </div>
        );
    }

    /**
     * Renders image content based on the configuration.
     */
    private _getImageContent(): JSX.Element {
        if (this._shouldUseThumbnails) {
            return this._getImageContentWithThumbnail();
        }
        return this._getImageContentWithoutThumbnail();
    }

    /**
     * Retrieves image content for the case when thumbnails approach should be used.
     */
    private _getImageContentWithThumbnail(): JSX.Element {
        // If thumbnail image failed to be loaded, main image is most likely to fail too, so empty placeholder is displayed.
        if (this.state.isThumbnailLoadingFailed || this.state.isMainImageLoadingFailed) {
            return this._renderEmptyPlaceholderImage();
        }

        if (this.state.isMainImageLoadingFinished) {
            return (
                <>
                    {this._renderLoadingPlaceholderImage(this.state.isLoadingPlaceholderHidden)}
                    {this._renderThumbnailImage(false, this.state.isThumbnailHidden)}
                    {this._renderMainImage()}
                </>
            );
        }

        if (this.state.isThumbnailLoadingFinished) {
            return (
                <>
                    {this._renderLoadingPlaceholderImage(this.state.isLoadingPlaceholderHidden)}
                    {this._renderThumbnailImage()}
                    {this._renderMainImage(true)}
                </>
            );
        }

        return (
            <>
                {this._renderLoadingPlaceholderImage()}
                {this._renderThumbnailImage(true)}
            </>
        );
    }

    /**
     * Retrieves image content for the case when thumbnails approach should NOT be used, and thumbnails should be skipped.
     */
    private _getImageContentWithoutThumbnail(): JSX.Element {
        if (this.state.isMainImageLoadingFailed) {
            return this._renderEmptyPlaceholderImage();
        }

        if (this.state.isMainImageLoadingFinished) {
            return (
                <>
                    {this._renderLoadingPlaceholderImage(this.state.isLoadingPlaceholderHidden)}
                    {this._renderMainImage()}
                </>
            );
        }

        return (
            <>
                {this._renderLoadingPlaceholderImage()}
                {this._renderMainImage(true)}
            </>
        );
    }

    /**
     * Initializes a timer to hide loading placeholder.
     */
    private _setLoadingPlaceholderHideTimeout(): void {
        if (this._hideLoadingPlaceholderTimeout) {
            clearTimeout(this._hideLoadingPlaceholderTimeout);
        }
        this._hideLoadingPlaceholderTimeout = setTimeout(
            () => this.setState({ isLoadingPlaceholderHidden: true }),
            Image._defaultLoadingPlaceholderHidingDelay
        );
    }

    /**
     * Initializes a timer to hide thumbnail.
     */
    private _setThumbnailHideTimeout(): void {
        if (this._hideThumbnailTimeout) {
            clearTimeout(this._hideThumbnailTimeout);
        }
        this._hideThumbnailTimeout = setTimeout(() => this.setState({ isThumbnailHidden: true }), Image._defaultThumbnailHidingDelay);
    }

    /**
     * Renders empty placeholder.
     */
    private _renderEmptyPlaceholderImage(): JSX.Element {
        return <EmptyPlaceholderImage className={classnames(Image._imageClassName, this.props.className)} />;
    }

    /**
     * Renders loading placeholder.
     * @param {boolean} isHidden - Specifies if the component should be hidden on the page. False by default.
     */
    private _renderLoadingPlaceholderImage(isHidden: boolean = false): JSX.Element {
        return (
            <LoadingPlaceholderImage
                className={classnames(Image._imageClassName, isHidden ? Image._hiddenStateClassName : '', this.props.className)}
            />
        );
    }

    /**
     * Renders thumbnail.
     * @param {boolean} isLoading - Specifies if the component is not ready to be displayed and in progress of downloading. False by default.
     * @param {boolean} isHidden - Specifies if the component should be hidden on the page. False by default.
     */
    private _renderThumbnailImage(isLoading: boolean = false, isHidden: boolean = false): JSX.Element {
        return (
            <ThumbnailImage
                {...this.props}
                src={this.state.src}
                key={this.state.src}
                className={classnames(
                    Image._imageClassName,
                    isLoading ? Image._loadingStateClassName : '',
                    isHidden ? Image._hiddenStateClassName : '',
                    this.props.className
                )}
                shouldHideOnFailure={true}
                onWillRender={this.onThumbnailImageUpdate}
            />
        );
    }

    /**
     * Renders main image.
     * @param {boolean} isLoading - Specifies if the component is not ready to be displayed and in progress of downloading. False by default.
     * @param {boolean} isSkipping - Specifies if the new image component should not be used.
     */
    private _renderMainImage(isLoading: boolean = false, isSkipping: boolean = false): JSX.Element {
        return (
            <MainImage
                {...this.props}
                src={this.state.src}
                key={this.state.src}
                className={classnames(Image._imageClassName, isLoading ? Image._loadingStateClassName : '', this.props.className)}
                shouldHideOnFailure={!isSkipping}
                onWillRender={this.onMainImageUpdate}
            >
                {this.props.children}
            </MainImage>
        );
    }
}
